import {type JSONRequestInit, reactFetchJSON} from '@github-ui/verified-fetch'

type GraphQLError = {
  readonly message?: string
}

type GraphQLResponse<TData> = {
  readonly data?: TData
  readonly errors?: readonly GraphQLError[]
}

export class IssueLifecyclePayloadError extends Error {
  readonly messages: readonly string[]

  constructor(messages: readonly string[], fallbackMessage: string) {
    super(messages[0] ?? fallbackMessage)
    this.name = 'IssueLifecyclePayloadError'
    this.messages = messages
  }
}

export async function submitIssueLifecycleMutation<TData>({
  path,
  method,
  queryId,
  input,
  errorMessage,
  isSuccess,
  payloadErrors,
}: {
  path: string
  method: 'DELETE' | 'POST'
  queryId: string
  input?: object
  errorMessage: string
  isSuccess: (data: TData) => boolean
  payloadErrors?: (data: TData) => ReadonlyArray<{readonly message: string}> | null | undefined
}): Promise<TData> {
  const request: JSONRequestInit = {
    method,
    body: input ? {input} : undefined,
  }
  const search = new URLSearchParams({queryId})
  const response = await reactFetchJSON(`${path}?${search.toString()}`, request)

  if (!response.ok) {
    throw new Error(`${errorMessage} (${response.status})`)
  }

  const json = (await response.json()) as GraphQLResponse<TData>
  if (json.errors?.length) {
    throw new Error(json.errors[0]?.message ?? errorMessage)
  }
  if (!json.data) {
    throw new Error(errorMessage)
  }

  const messages = payloadErrors?.(json.data)?.map(error => error.message) ?? []
  if (messages.length > 0) {
    throw new IssueLifecyclePayloadError(messages, errorMessage)
  }
  if (!isSuccess(json.data)) {
    throw new Error(errorMessage)
  }

  return json.data
}

export function issueLifecyclePath(owner: string, repository: string, number: number) {
  return `/${encodeURIComponent(owner)}/${encodeURIComponent(repository)}/issues/${number}`
}
